import { Component, Input, EventEmitter, Output, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { DateTime } from 'luxon';
import { toLower, toNumber } from 'lodash-es';
import { SettingsService } from 'services/settings.service';
import { NavController } from 'providers/nav-controller';
import { IInputCalendarPage } from 'pages/input-calendar-page/input-calendar-interface';
import { formatShortDate } from 'util/formatting';
import { lockClick } from 'directives/cs-capture-cover/cs-capture-cover.directive';

import { LL } from 'i18n/i18n.service';
@Component({
    selector: 'input-calendar',
    templateUrl: 'input-calendar.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class InputCalendarComponent {
  protected LL = LL;
  @Input() date: string;
  // despite the name, this is never rendered as display text (the day/month/year
  // inputs use their own LL.common.placeholders.* text) -- it is an ISO-date
  // fallback value used only in handleCalendarClickAsync(), so it is NOT retyped
  // to TranslatedString per the Phase 1 "not display text" exclusion
  @Input() placeholder: string;
  @Input() maxDate: DateTime;
  @Input() minDate: DateTime;
  @Output() dateChange: EventEmitter<string> = new EventEmitter<string>();
  textYear: string = '';
  textMonth: string = '';
  textDay: string = '';

  day: number;
  month: number;
  year: number;

  separator: string = '/';
  formatOrders: string[] = [];

  months = [
    {
      name: "January",
      short: "Jan",
    },
    {
      name: "February",
      short: "Feb"
    },
    {
      name: "March",
      short: "Mar",
    },
    {
      name: "April",
      short: "Apr",
    },
    {
      name: "May",
      short: "May"
    },
    {
      name: "June",
      short: "Jun"
    },
    {
      name: "July",
      short: "Jul"
    },
    {
      name: "August",
      short: "Aug"
    },
    {
      name: "September",
      short: "Sep",
    },
    {
      name: "October",
      short: "Oct"
    },
    {
      name: "November",
      short: "Nov"
    },
    {
      name: "December",
      short: "Dec"
    }
  ]

  constructor(
    private navCtrl: NavController,
    private cdr: ChangeDetectorRef,
    private settingsService: SettingsService
  ) { }

  daysInMonth(month: number, year: number) {
    if (year == undefined) {
      year = 2020;
    }
    let dt = DateTime.local(year, month, 1);
    return dt.daysInMonth;
  }

  handleDayChange(ev: Event) {
    this.textDay = (ev.target as HTMLInputElement).value;

    if (this.textDay !== "") {
      let day = toNumber(this.textDay);
      if (isNaN(day)) {
        // if component was never initialized with a valid date, this.day is undefined
        if (this.day == undefined) {
          this.textDay = '';
          return;
        }
        day = this.day;
        this.textDay = day.toFixed(0);
      } else {
        this.textDay = day.toFixed(0);
        this.day = day;
      }
    } else {
      this.day = undefined;
    }

    this.onDateChange();
  }

  handleYearChange(ev: Event) {
    this.textYear = (ev.target as HTMLInputElement).value;
    if (this.textYear !== "") {
      let year = toNumber(this.textYear);
      if (isNaN(year)) {
        // if component was never initialized with a valid date, this.year is undefined
        if (this.year == undefined) {
          this.textYear = '';
          return;
        }
        year = this.year;
        this.textYear = year.toFixed(0);
      } else {
        this.textYear = year.toFixed(0);
        this.year = year;
      }
    } else {
      this.year = undefined;
    }

    this.onDateChange();
  }

  handleMonthChange(ev: Event) {
    this.textMonth = (ev.target as HTMLInputElement).value;
    if (this.textMonth !== "") {
      let number = toNumber(this.textMonth);
      if (isNaN(number)) {
        for (let iMonth = 0; iMonth < this.months.length; ++iMonth) { 
          if (toLower(this.months[iMonth].short) === toLower(this.textMonth) ||
            toLower(this.months[iMonth].name) === toLower(this.textMonth)) { 
            this.month = iMonth + 1;
            this.textMonth = this.months[iMonth].short;
            break;
          }
        }
      } else {
        if (number >= 1 && number <= 12) {
          this.textMonth = this.months[number - 1].short;
        } else if (number > 12) {
          this.textMonth = this.months[11].short;
        } else if (number < 1) { 
          this.textMonth = this.months[0].short;
        }

        for (let iMonth = 0; iMonth < this.months.length; ++iMonth) { 
          if (this.months[iMonth].short === this.textMonth) { 
            this.month = iMonth + 1;
            break;
          }
        }
      }
    } else {
      this.month = undefined;
    }

    this.onDateChange();
  }

  onMonthInputClick(e: Event) {
    this.onMonthInputFocus(e);
  }

  onMonthInputFocus(e: Event) {
    e.stopPropagation();
    if (this.textMonthPrev === '') {
      this.textMonthPrev = this.textMonth;
    }
    this.textMonth = '';
  }

  textMonthPrev: string = ""

  onMonthInputBlur(e: Event) {
    void e;
    if (!this.textMonth || this.textMonth === '') {
      this.textMonth = this.textMonthPrev;
    }
    this.textMonthPrev = '';
  }

  ngOnInit() {
    // use formatShortDate to respect user's dateFormat setting
    let formatExampleDate = formatShortDate(DateTime.local(2021, 12, 31), this.settingsService);
    let matchResult = formatExampleDate.match(/([0-9]+)\s?([^0-9\s]*)\s*([0-9]+)[^0-9\s]*\s*([0-9]+)/);
    if (matchResult) {
      for (let iMatch = 1; iMatch <= 4; ++iMatch) {
        if (iMatch !== 2) {
          // handle both 2-digit ('21') and 4-digit ('2021') year formats
          if (matchResult[iMatch] === '2021' || matchResult[iMatch] === '21') {
            this.formatOrders.push('year');
          } else if (matchResult[iMatch] === '12') {
            this.formatOrders.push('month');
          } else if (matchResult[iMatch] === '31') {
            this.formatOrders.push('day');
          }
        } else {
          this.separator = matchResult[2];
        }
      }
    } else {
      this.formatOrders = ['month', 'day', 'year'];
      this.separator = '/';
    }

    // this assures we get some valid format even if the locale is some weird one
    if (this.formatOrders.length !== 3) {
      this.formatOrders = ['month', 'day', 'year'];
      this.separator = '/';
    }

    if (!this.date) {
      return;
    }

    let texts = this.dateToTexts(this.date);
    if (texts) {
      this.textDay = texts.textDay;
      this.textMonth = texts.textMonth;
      this.textYear = texts.textYear;
    }

    let numbers = this.dateToNumbers(this.date);
    if (numbers) {
      this.day = numbers.day;
      this.month = numbers.month;
      this.year = numbers.year;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    void changes;
    let texts = this.dateToTexts(this.date);
    if (texts) {
      this.textDay = texts.textDay;
      this.textMonth = texts.textMonth;
      this.textYear = texts.textYear;
    }

    let numbers = this.dateToNumbers(this.date);
    if (numbers) {
      this.day = numbers.day;
      this.month = numbers.month;
      this.year = numbers.year;
    }
  }

  dateToTexts(date: string): { textYear: string; textMonth: string; textDay: string } | null {
    let dt = DateTime.fromISO(date);
    if (!dt.isValid) {
      return null;
    }

    return {
      textYear: dt.year.toFixed(0),
      textMonth: this.months[dt.month - 1].short,
      textDay: dt.day.toFixed(0),
    };
  }

  dateToNumbers(date: string): { year: number; month: number; day: number } | null {
    let dt = DateTime.fromISO(date);
    if (!dt.isValid) {
      return null;
    }

    return {
      year: dt.year,
      month: dt.month,
      day: dt.day,
    };
  }

  fixDateIfNeeded() {
    if (this.day) {
      let daysInMonth = this.daysInMonth(this.month, this.year);
      if (this.day < 1) {
        this.day = 1;
        this.textDay = "1";
      } else if (this.day > daysInMonth) {
        this.day = daysInMonth;
        this.textDay = daysInMonth.toFixed(0);
      }
    }
  }

  onDateChange() {
    this.fixDateIfNeeded();

    if (this.year && this.month && this.day) {
      let date = DateTime.local(this.year, this.month, this.day).toISODate();
      if (date != null) {
        this.date = date;
        this.dateChange.emit(this.date);
        console.log(this.date);
      }
    }
  }

  handleCalendarClick(event: Event) {
    event.stopPropagation(); // prevents bubbling when control is part of a bigger clickable control
    void this.handleCalendarClickAsync();
  }

  async handleCalendarClickAsync() {
    const result = await this.navCtrl.callAsync(IInputCalendarPage, {
      dateISO: this.date || this.placeholder,
      minDateISO: this.minDate?.toISODate(),
      maxDateISO: this.maxDate?.toISODate(),
    });
    if (result?.dateISO) {
      this.onPopoverDismiss(result.dateISO);
    }
  }

  onPopoverDismiss(date: string) {
    if (date != null) {
      this.date = date;
      this.dateChange.emit(date);
      console.log(this.date);
    }

    let texts = this.dateToTexts(this.date);
    if (texts) {
      this.textDay = texts.textDay;
      this.textMonth = texts.textMonth;
      this.textYear = texts.textYear;
    }

    let numbers = this.dateToNumbers(this.date);
    if (numbers) {
      this.day = numbers.day;
      this.month = numbers.month;
      this.year = numbers.year;
    }
    
    this.cdr.markForCheck();
  }

  handleButtonClick(event: Event) {
    event.stopPropagation();
    lockClick(event, this.handleCalendarClickAsync());
  }
}
